import {styleMap} from '@script/colorz/configMap';
import {
	MarkerClusterer,
	SuperClusterAlgorithm,
	GridAlgorithm,
} from '@googlemaps/markerclusterer';
import emitter from '@utils/Emitter.js';

export default class StoreLocator extends HTMLElement {
	constructor() {
		super();
		this.el = this;
		this.elStoreSearch = this.querySelector('[data-storloc-search]');
		this.elStoreResult = this.querySelector('[data-storloc-result]');
		this.elStoreResultList = this.querySelector('[store-locator-result--list]');
		this.elStoreMap = this.querySelector('[data-storloc-map]');
		this.filters = this.querySelectorAll('[data-filter]');

		this.elForm = this.elStoreSearch.querySelector('form');

		this.map = [];
		this.markers = [];
		this.styleMap = styleMap;
		this.geocoder = [];
		this.clusters = null;
		this.resetMarkers = true;
		this.center = {
			lat: 48.8575,
			lng: 2.3514,
		};

		this.stores = window.storelocator.stores;
		this.mapContainer = this.el.querySelector('[data-google-map]');
		this.listStores = this.el.querySelectorAll('[data-store]');

		this.submitButton = this.el.querySelector('[data-search-btn]');
		this.inputText = this.el.querySelector('[data-input-text]');
		this.selectRadius = this.el.querySelector('[data-select-radius]');
		this.localize = this.el.querySelector('[data-localize]');
		this.isList = this.el.querySelector('[data-list]');
		this.isMap = this.el.querySelector('[data-map]');
		this.storesContainer = this.el.querySelector('[data-store-list]');

		this.activeFilters = {};

		this.filterCheckboxes = {};

		this.filters.forEach((filter) => {
			const id = filter.getAttribute('id');
			if (id) {
				this.filterCheckboxes[id] = filter;
				this.activeFilters[id] = false;
			}
		});
	}

	initMap() {
		this.map = new google.maps.Map(this.elStoreMap, {
			center: this.center,
			zoom: 10,
			mapTypeId: 'roadmap',
			styles: this.styleMap,
			mapTypeControl: false,
		});

		this.geocoder = new google.maps.Geocoder();
		this.infoWindow = new google.maps.InfoWindow();
		this.clearLocations();
		this.showStoresMarkers(this.stores);
		this.setOnClickListener();
	}

	connectedCallback() {
		this.elForm.addEventListener('submit', (e) => {
			this.getStoresList(e);
		});

		this.initMap();

		google.maps.event.addListenerOnce(this.map, 'idle', () =>
			//Quand la map est correctement initialiser, on set les clusters
			this.showVisibleMarkers(this.map, this.markers, this.listStores),
		);

		this.inputText.addEventListener('input', () => {
			if (this.inputText.value.trim() === '') {
				this.submitButton.setAttribute('disabled', 'true');
			} else {
				this.submitButton.removeAttribute('disabled');
			}
		});

		this.submitButton.addEventListener('click', () => {
			this.geocode({address: this.inputText.value}, this.map);
		});

		this.inputText.addEventListener('keypress', (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				this.geocode({address: this.inputText.value}, this.map);
			}
		});

		if (this.localize) {
			this.localize.addEventListener('click', () => {
				this.localizeUser(this.map);
			});
		}

		Object.values(this.filterCheckboxes).forEach((checkbox) => {
			checkbox.addEventListener('change', () => {
				this.activeFilters[checkbox.id] = checkbox.checked;

				//A chaque clique sur un filtre, on reset les markers
				this.showVisibleMarkers(this.map, this.markers, this.listStores);
			});
		});

		if (window.innerWidth < 993) {
			this.elStoreMap.classList.add('is-mobile');
			this.elStoreResult.classList.add('is-mobile');
			this.switchDisplay();
		} else {
			this.elStoreMap.classList.remove('is-mobile');
			this.elStoreResult.classList.remove('is-mobile');
		}
	}

	/**
	 * Handles the store locator form submission, geocodes the input address,
	 * constructs a request URL with the geocoded coordinates and selected radius,
	 * fetches the list of stores from the server, and updates the UI with the results.
	 *
	 * @async
	 * @param {Event} e - The form submission event.
	 * @returns {Promise<void>}
	 */
	async getStoresList(e) {
		e.preventDefault();
		const radius = this.selectRadius.value;

		const geocoder = new google.maps.Geocoder();
		const location = await geocoder.geocode(
			{address: this.inputText.value},
			(results, status) => {
				if (status === 'OK') {
					const location = results[0].geometry.location;
					return {
						latitude: location.lat(),
						longitude: location.lng(),
					};
				} else {
					console.error('Geocode failed: ' + status);
				}
			},
		);

		const url = new URL(`${window.shopUrl}/apps/clrz/storelocator-v2`);

		if (location.results && location.results.length > 0) {
			url.searchParams.set(
				'latitude',
				location.results[0].geometry.location.lat(),
			);
			url.searchParams.set(
				'longitude',
				location.results[0].geometry.location.lng(),
			);
		}

		if (radius !== '0') {
			url.searchParams.set('radius', radius);
		}

		fetch(url)
			.then((response) => {
				return response.json();
			})
			.then((data) => {
				this.updateList(data);
				this.showStoresMarkers(data.stores, true);
				this.setOnClickListener(true, data.stores);
			})
			.catch((error) => {
				console.error('Error:', error);
			});
	}

	updateList(data) {
		this.elStoreResultList.innerHTML = '';

		data.stores.forEach((store) => {
			const div = document.createElement('div');
			div.className =
				'u-flex u-py-20 u-px-5 u-gap-x-20 u-border-b u-border-solid u-border-grey';
			div.style.display = 'grid';
			div.setAttribute('data-store', '');

			div.innerHTML = `
			<p class="store-locator-result__address t-text u-text-blue">
				<span class="t-small-title t-small-title--12">
				${store.display_name}
				</span>
				<br>
				${store.street}
				<br>
				${store.zip_code}
				<br>
				${store.city}
				<br>
				${store.country}
			</p>
			`;

			this.elStoreResultList.appendChild(div);
		});
	}

	geocode(request) {
		this.geocoder
			.geocode(request)
			.then((result) => {
				const {results} = result;
				this.map.setCenter(results[0].geometry.location);
				this.map.setZoom(10);
				this.showVisibleMarkers(this.map, this.markers, this.listStores);
				return results;
			})
			.catch((e) => {
				this.map.setCenter(this.center);
				this.map.setZoom(5);
				this.showVisibleMarkers(this.map, this.markers, this.listStores);
				console.log(
					'Geocode was not successful for the following reason: ' + e,
				);
			});
	}

	setOnClickListener(fromProxy, listStores) {
		let stores = this.el.querySelectorAll('[data-store]');

		stores.forEach((el, i) => {
			el.addEventListener('click', () => {
				if (fromProxy) {
					var latitude = listStores[i].latitude;
					var longitude = listStores[i].longitude;
				} else {
					var latitude = this.stores[i].coordinates.latitude;
					var longitude = this.stores[i].coordinates.longitude;
				}
				let storeLatlng = {
					lat: parseFloat(latitude),
					lng: parseFloat(longitude),
				};
				this.map.setCenter(storeLatlng);
				this.map.setZoom(16);
				setTimeout(() => {
					google.maps.event.trigger(this.markers[i], 'click');
				}, 500);
				this.displayMap();
			});
			emitter.emit('store:clicked');
		});
	}

	showStoresMarkers(stores, fromProxy) {
		var bounds = new google.maps.LatLngBounds();
		stores.forEach((store) => {
			if (fromProxy) {
				var latlng = new google.maps.LatLng(store.latitude, store.longitude);
				var street = store.street;
				var zip = store.zip_code;
				var city = store.city;
			} else {
				var latlng = new google.maps.LatLng(
					store.coordinates.latitude,
					store.coordinates.longitude,
				);
				var street = store.address.street;
				var zip = store.address.zip_code;
				var city = store.address.city;
				var type = store.type;
			}
			var name = store.name;
			var phone = store.phoneNumber;
			bounds.extend(latlng);
			this.createMarker(latlng, name, street, city, zip, phone, type);
		});
		this.map.fitBounds(bounds);
	}

	createMarker(latlng, name, street, city, zip, phone, type) {
		//HTML pour l'affichage des infos des stores sur la map
		var html = `
    <div class="u-text-black">
    <div class="surtitle">
      ${name}
    </div> `;

		html += `
		<div class="u-mt-4 u-mb-8">
				<span class="text u-block">${street}</span>
				<span class="text u-block u-uppercase">${zip} ${city}</span>
				<span class="text u-block">${phone}</span>
		</div>
		`;

		html += `
			<div class="u-flex u-justify-between u-mt-4 u-pt-10 u-border-t u-border-t-black-15">
				<a class="t-link t-link--underline" href="https://www.google.com/maps/dir/?api=1&destination=${latlng}" target="_blank">${window.storelocator.itinerary}</a>
			</div>
		</div>
    `;

		//Changement de l'icon sur la map
		var mainIcon = {
			url: 'https://cdn.shopify.com/s/files/1/0438/7795/7785/files/map-pin-lightblue.png?v=1748269008',
			scaledSize: new google.maps.Size(42, 57),
		};

		//Création du marker avec les infos lié.
		var marker = new google.maps.Marker({
			map: this.map,
			position: latlng,
			icon: mainIcon,
			visible: true,
			type: type,
		});

		google.maps.event.addListener(marker, 'click', () => {
			this.infoWindow.setContent(html);
			this.infoWindow.open(this.map, marker);
		});
		this.markers.push(marker);
	}

	showVisibleMarkers(map, markers, stores) {
		emitter.on('store:clicked', () => {
			//Si on clique sur un store de la liste, on ne reset pas les markers/clusters.
			this.resetMarkers = false;
		});

		if (this.resetMarkers == true) {
			//On reset les clusters à chaque fois pour qu'ils affichent le bon nombre de marker
			if (this.clusters != null) {
				this.clusters.setMap(null);
			}

			//Récupéreation des limite de la map
			var bounds = map.getBounds();
			if (bounds) {
				var visibleMarkers = [];

				markers.forEach((marker, index) => {
					var store = stores[index];
					var isVisible = true;

					for (const filter in this.activeFilters) {
						if (this.activeFilters.hasOwnProperty(filter)) {
							const handleize_type = marker['type']
								.toLowerCase()
								.replace(/ /g, '-');
							if (this.activeFilters[filter] && handleize_type != filter) {
								//Si le marker ne possède pas le filtre actif à true on set la variable isVisible false
								isVisible = false;
								break;
							}
						}
					}

					if (isVisible && bounds.contains(marker.getPosition())) {
						//Si isVisible est true et que le marker est contenu dans les limites de la map, on affiche le store dans la liste et on affiche le marker sur la map
						store.style.display = 'grid';
						marker.setVisible(true);
						visibleMarkers.push(marker); // Add marker to visibleMarkers array
					} else {
						store.style.display = 'none';
						marker.setVisible(false);
					}
				});

				//Création des clusters avec les markers visibles dans la map
				this.clusters = new MarkerClusterer({
					map: map,
					markers: visibleMarkers,
					algorithm: new GridAlgorithm({
						gridSize: 60,
					}),
					renderer: {
						render: ({markers, position}) => {
							return new google.maps.Marker({
								position,
								optimized: true,
								label: {
									text: String(markers.length),
									color: 'white',
								},
								icon: 'https://cdn.shopify.com/s/files/1/0611/6307/7732/files/cluster_01_1.png?v=1712048346',
							});
						},
					},
				});

				//Calcule du nombre de boutique disponibles en fonction de la recherche effectuée
				let count = visibleMarkers.length;
				if (this.el.querySelector('[data-count]')) {
					if (count > 1) {
						this.el.querySelector('[data-count]').innerHTML =
							count + ' ' + window.storelocator.shops;
					} else if (count == 1) {
						this.el.querySelector('[data-count]').innerHTML =
							count + ' ' + window.storelocator.shop;
					} else {
						this.el.querySelector('[data-count]').innerHTML =
							window.storelocator.no_shop;
					}
				}
			}
		}

		this.resetMarkers = true;
	}

	clearLocations() {
		this.infoWindow.close();
		for (var i = 0; i < this.markers.length; i++) {
			this.markers[i].setMap(null);
		}
		this.markers.length = 0;
	}

	//Permet de géocaliser l'utilisateur sur la map
	localizeUser(map) {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const pos = {
						lat: position.coords.latitude,
						lng: position.coords.longitude,
					};
					map.setCenter(pos);
					map.setZoom(12);
					this.showVisibleMarkers(map, this.markers, this.listStores);
				},
				() => {
					alert('Impossible de vous géolocaliser');
				},
			);
		} else {
			// Browser doesn't support Geolocation
			alert("Browser doesn't support Geolocation");
		}
	}

	//Sur mobile, permet de switcher entre la liste de store et la map
	switchDisplay() {
		this.elStoreResultList.addEventListener('click', () => {
			this.displayList();
		});
		this.elStoreMap.addEventListener('click', () => {
			this.displayMap();
		});
	}

	displayList() {
		if (!this.elStoreResult.classList.contains('in-view')) {
			this.elStoreResult.classList.add('in-view');
			this.elStoreResult.classList.remove('is-hidden');
			this.elStoreMap.classList.add('is-hidden');
			this.elStoreMap.classList.remove('in-view');
			this.elStoreResultList.classList.toggle('selected');
			this.elStoreMap.classList.toggle('selected');
		}
	}

	displayMap() {
		if (!this.elStoreMap.classList.contains('in-view')) {
			this.elStoreResult.classList.add('is-hidden');
			this.elStoreResult.classList.remove('in-view');
			this.elStoreMap.classList.add('in-view');
			this.elStoreMap.classList.remove('is-hidden');
			this.elStoreMap.classList.toggle('selected');
			this.elStoreResultList.classList.toggle('selected');
		}
	}
}
